import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import {
  NOTIFICATIONS_PAGE_PARAM,
  type NotificationDetailData,
  type NotificationListData,
  type NotificationListItem,
} from '../resident-notifications/notification-api';
import { isManagerNotificationsMockEnabled } from './is-manager-notifications-mock';
import {
  getMockManagerNotificationDetail,
  getMockManagerNotificationList,
  mockManagerNotificationRecipients,
  submitMockManagerNotification,
} from './mocks';
import type { NotificationRecipientsData } from './types';

export { NOTIFICATIONS_PAGE_PARAM };

export type ManagerNotificationListItem = NotificationListItem & {
  to?: string | string[];
  from?: string;
  author_id?: number;
  resident_id?: number;
};

export const MANAGER_LK_NOTIFICATIONS_TAB_ID = 2;
export const MANAGER_NOTIFICATIONS_SEARCH_ID = 'managerNotificationsSearch';

type ApiSuccessResponse<T> = {
  status: 'success';
  data?: T;
  msg?: T;
};

type ApiErrorResponse = {
  status: 'error';
  msg: string;
};

type ApiMessageResponse = {
  status: 'success';
  msg: string;
};

type ApiResidentsAndClusters = {
  residents?: Array<{ id: number | string; name: string }>;
  clusters?: Array<{ id: number | string; name: string }>;
};

export function buildManagerNotificationListUrl(params: {
  q?: string;
  page?: number;
  residentId?: string | null;
}): string {
  const url = new URL(getApiUrl('/api/personal/notification/'), window.location.origin);

  const query = params.q?.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (params.residentId && params.residentId !== '-1') {
    url.searchParams.set('residentId', params.residentId);
  }

  if (params.page && params.page > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${params.page}`);
  }

  return url.toString();
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();

  try {
    return JSON.parse(text) as T;
  } catch {
    throw new Error('Сервер вернул некорректный ответ');
  }
}

function getSuccessPayload<T>(result: ApiSuccessResponse<T>): T {
  if (result.data !== undefined) {
    return result.data;
  }

  if (result.msg !== undefined) {
    return result.msg;
  }

  throw new Error('Сервер вернул некорректный ответ');
}

export async function fetchManagerNotificationList(params: {
  q?: string;
  page?: number;
  residentId?: string | null;
}): Promise<NotificationListData> {
  if (isManagerNotificationsMockEnabled()) {
    return getMockManagerNotificationList();
  }

  const response = await fetch(buildManagerNotificationListUrl(params), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const result = await parseJsonResponse<ApiSuccessResponse<NotificationListData> | ApiErrorResponse>(
    response,
  );

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : 'Не удалось загрузить список уведомлений',
    );
  }

  return getSuccessPayload(result);
}

export async function fetchManagerNotificationDetail(id: number): Promise<NotificationDetailData> {
  if (isManagerNotificationsMockEnabled()) {
    return getMockManagerNotificationDetail(id);
  }

  const response = await fetch(getApiUrl(`/api/personal/notification/${id}/`), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const result = await parseJsonResponse<ApiSuccessResponse<NotificationDetailData> | ApiErrorResponse>(
    response,
  );

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : 'Не удалось загрузить уведомление',
    );
  }

  return getSuccessPayload(result);
}

export async function markManagerNotificationRead(id: number): Promise<string> {
  if (isManagerNotificationsMockEnabled()) {
    return 'Сообщение отмечено как прочитанное';
  }

  const sessid = getBitrixSessid();

  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const response = await fetch(getApiUrl(`/api/personal/notification/${id}/`), {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-Bitrix-Csrf-Token': sessid,
    },
    body: JSON.stringify({ sessid }),
  });

  const result = await parseJsonResponse<ApiMessageResponse | ApiErrorResponse>(response);

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : 'Не удалось пометить уведомление прочитанным',
    );
  }

  return result.msg;
}

export async function fetchManagerNotificationRecipients(): Promise<NotificationRecipientsData> {
  if (isManagerNotificationsMockEnabled()) {
    return mockManagerNotificationRecipients;
  }

  const response = await fetch(getApiUrl('/api/personal/notification/get-residents/'), {
    method: 'GET',
    credentials: 'same-origin',
  });

  const result = await parseJsonResponse<ApiSuccessResponse<ApiResidentsAndClusters> | ApiErrorResponse>(
    response,
  );

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : 'Не удалось загрузить список резидентов',
    );
  }

  const data = getSuccessPayload(result);

  return {
    residents: (data.residents ?? []).map((item) => ({
      id: String(item.id),
      name: item.name,
    })),
    clusters: (data.clusters ?? []).map((item) => ({
      id: String(item.id),
      name: item.name,
    })),
  };
}

export async function submitManagerNotification(payload: {
  subject: string;
  message: string;
  residents?: string[];
  clusters?: string[];
  files?: File[];
}): Promise<void> {
  if (isManagerNotificationsMockEnabled()) {
    await submitMockManagerNotification();
    return;
  }

  const sessid = getBitrixSessid();
  if (!sessid) {
    throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
  }

  const body = new FormData();
  body.append('sessid', sessid);
  body.append('subject', payload.subject);
  body.append('message', payload.message);

  payload.residents?.forEach((id) => body.append('residents[]', id));
  payload.clusters?.forEach((id) => body.append('clusters[]', id));
  payload.files?.forEach((file) => body.append('replyAttachments[]', file));

  const response = await fetch(getApiUrl('/api/personal/notification/'), {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'X-Bitrix-Csrf-Token': sessid,
    },
    body,
  });

  const result = await parseJsonResponse<ApiMessageResponse | ApiErrorResponse>(response);

  if (!response.ok || result.status !== 'success') {
    throw new Error(
      result.status === 'error' && result.msg ? result.msg : 'Не удалось отправить уведомление',
    );
  }
}
